import React, { useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

import "./CareerMap.css";

import SimpleLine from "../../utils/simpleline";

interface CareerMapProps {
  score: number[]; // [lbi, rbi, pei, ai, hi, wci]
  highlightIndex?: number | null;
}

const LINE_COLORS = ["#000000", "#FF6384", "#FF9F40"];

const CareerMap: React.FC<CareerMapProps> = ({ score, highlightIndex }) => {
  const { t } = useTranslation("careerMap");

  // 좌뇌/우뇌 판별 (lbi = score[0])
  // lbi < 12이면 우뇌(Right Brain), 12이상이면 좌뇌(Left Brain)
  const lbi = score[0] || 0;
  const isRightBrain = lbi < 12;
  const brainKey = isRightBrain ? "rightBrain" : "leftBrain";

  // 카테고리별 섹션 구성 데이터
  const CATEGORY_SETS = useMemo(
    () => [
      {
        // 인지성향 (lbi)
        title: t("cognitiveStructure.title"),
        sections: [
          { label: t("cognitiveStructure.leftBrainAnalytical"), count: 3 },
          { label: t("cognitiveStructure.leftBrainInquisitive"), count: 2 },
          { label: t("cognitiveStructure.leftBrainExperimental"), count: 3 },
          { label: t("cognitiveStructure.leftBrainConceptual"), count: 2 },
          { label: t("cognitiveStructure.rightBrainLogical"), count: 3 },
          { label: t("cognitiveStructure.rightBrainStrategic"), count: 3 },
          { label: t("cognitiveStructure.rightBrainCommunicative"), count: 3 },
          { label: t("cognitiveStructure.rightBrainIntuitive"), count: 3 },
        ],
      },
      {
        // 인지환경 (rbi)
        title: t("cognitiveEnvironment.title"),
        sections: isRightBrain
          ? [
              {
                label: t(
                  `cognitiveEnvironment.${brainKey}.artisticImagination`,
                ),
                count: 5,
              },
              {
                label: t(`cognitiveEnvironment.${brainKey}.emotionRich`),
                count: 5,
              },
              {
                label: t(`cognitiveEnvironment.${brainKey}.emotionModerated`),
                count: 6,
              },
              {
                label: t(`cognitiveEnvironment.${brainKey}.realityBased`),
                count: 6,
              },
            ]
          : [
              {
                label: t(`cognitiveEnvironment.${brainKey}.scienceImagination`),
                count: 5,
              },
              {
                label: t(`cognitiveEnvironment.${brainKey}.creativePlanning`),
                count: 5,
              },
              {
                label: t(`cognitiveEnvironment.${brainKey}.logicOriented`),
                count: 6,
              },
              {
                label: t(`cognitiveEnvironment.${brainKey}.realityOriented`),
                count: 6,
              },
            ],
      },
      {
        // 신체활동성 (pei)
        title: t("physicalActivity.title"),
        sections: isRightBrain
          ? [
              {
                label: t(`physicalActivity.${brainKey}.physicalCare`),
                count: 5,
              },
              {
                label: t(`physicalActivity.${brainKey}.fieldOriented`),
                count: 5,
              },
              {
                label: t(`physicalActivity.${brainKey}.selectiveActivity`),
                count: 6,
              },
              {
                label: t(`physicalActivity.${brainKey}.staticActivity`),
                count: 6,
              },
            ]
          : [
              { label: t(`physicalActivity.${brainKey}.dynamic`), count: 5 },
              {
                label: t(`physicalActivity.${brainKey}.operational`),
                count: 5,
              },
              { label: t(`physicalActivity.${brainKey}.versatile`), count: 6 },
              { label: t(`physicalActivity.${brainKey}.static`), count: 6 },
            ],
      },
      {
        // 진취성 (ai)
        title: t("enterprising.title"),
        sections: isRightBrain
          ? [
              {
                label: t(`enterprising.${brainKey}.charismaticLeadership`),
                count: 5,
              },
              {
                label: t(`enterprising.${brainKey}.changeOrientedLeadership`),
                count: 5,
              },
              {
                label: t(`enterprising.${brainKey}.mediatingLeadership`),
                count: 6,
              },
              {
                label: t(`enterprising.${brainKey}.stableLeadership`),
                count: 6,
              },
            ]
          : [
              {
                label: t(`enterprising.${brainKey}.technoLeadership`),
                count: 5,
              },
              {
                label: t(`enterprising.${brainKey}.goalOrientedLeadership`),
                count: 5,
              },
              {
                label: t(`enterprising.${brainKey}.balancedLeadership`),
                count: 6,
              },
              {
                label: t(`enterprising.${brainKey}.expertLeadership`),
                count: 6,
              },
            ],
      },
      {
        // 사회성 (hi)
        title: t("sociality.title"),
        sections: isRightBrain
          ? [
              {
                label: t(`sociality.${brainKey}.expansiveRelationship`),
                count: 5,
              },
              {
                label: t(`sociality.${brainKey}.relationshipCentered`),
                count: 5,
              },
              {
                label: t(`sociality.${brainKey}.selectiveRelationship`),
                count: 6,
              },
              {
                label: t(`sociality.${brainKey}.introvertedRelationship`),
                count: 6,
              },
            ]
          : [
              { label: t(`sociality.${brainKey}.pureRelationship`), count: 5 },
              {
                label: t(`sociality.${brainKey}.relationshipOriented`),
                count: 5,
              },
              {
                label: t(`sociality.${brainKey}.cautiousRelationship`),
                count: 6,
              },
              {
                label: t(`sociality.${brainKey}.independentRelationship`),
                count: 6,
              },
            ],
      },
      {
        // 실행성 (wci)
        title: t("execution.title"),
        sections: isRightBrain
          ? [
              {
                label: t(`execution.${brainKey}.thoroughPerfectionism`),
                count: 5,
              },
              { label: t(`execution.${brainKey}.taskOriented`), count: 5 },
              {
                label: t(`execution.${brainKey}.selectiveExecution`),
                count: 6,
              },
              { label: t(`execution.${brainKey}.selfDirected`), count: 6 },
            ]
          : [
              {
                label: t(`execution.${brainKey}.meticulousPerfectionism`),
                count: 5,
              },
              { label: t(`execution.${brainKey}.missionDriven`), count: 5 },
              {
                label: t(`execution.${brainKey}.efficientExecution`),
                count: 6,
              },
              { label: t(`execution.${brainKey}.researchCentered`), count: 6 },
            ],
      },
    ],
    [t, isRightBrain, brainKey],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const linesRef = useRef<{ remove: () => void }[]>([]);

  useEffect(() => {
    linesRef.current.forEach((line) => line.remove && line.remove());
    linesRef.current = [];

    if (!score || score.length === 0) return;

    const dataSetIndex = 0;

    const pointElements = score.map((val, idx) => {
      const safeVal = Math.max(0, Math.min(21, Math.round(Number(val))));
      const id = `c${idx + 1}-${safeVal}`;
      return document.getElementById(id);
    });

    if (pointElements.some((el) => !el)) {
      console.warn("그래프 점 요소를 찾을 수 없어 선을 그릴 수 없습니다.");
      return;
    }

    const lineStyle = {
      type: "straight",
      width: 3,
      color: LINE_COLORS[dataSetIndex],
    };

    if (typeof SimpleLine === "undefined") {
      console.error("SimpleLine 라이브러리가 로드되지 않았습니다.");
      return;
    }

    for (let i = 0; i < pointElements.length; i++) {
      const el = pointElements[i] as HTMLElement;
      el.classList.add("active");

      if (i < pointElements.length - 1) {
        const nextEl = pointElements[i + 1] as HTMLElement;
        try {
          const myLine = new SimpleLine(el, nextEl, lineStyle);
          linesRef.current.push(myLine);
        } catch (e) {
          console.error(e);
        }
      }
    }

    return () => {
      linesRef.current.forEach((line) => line.remove && line.remove());
      pointElements.forEach((el) => {
        if (el) el.classList.remove("active");
      });
    };
  }, [score, CATEGORY_SETS]); // CATEGORY_SETS가 바뀔 때도 재조정

  return (
    <div className="career-map-container" ref={containerRef}>
      <div className="map-tables-wrapper">
        <table className="map-tables">
          <tbody>
            <tr className="tables">
              {CATEGORY_SETS.map((cat, catIdx) => (
                <td key={catIdx}>
                  <div
                    className={`map-table cat-${catIdx + 1} ${
                      highlightIndex === catIdx ? "highlight" : ""
                    }`}
                  >
                    {renderCategorySections(cat.sections, catIdx + 1)}
                  </div>
                </td>
              ))}
            </tr>
            <tr className="titles">
              {CATEGORY_SETS.map((cat, catIdx) => (
                <td key={catIdx}>
                  <div className={`map-title cat-${catIdx + 1}`}>
                    {cat.title}
                  </div>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const renderCategorySections = (
  sections: { label: string; count: number }[],
  catNum: number,
) => {
  let currentPoint = 21;

  return sections.map((section, secIdx) => {
    const points = [];
    for (let i = 0; i < section.count; i++) {
      points.push(
        <div
          key={currentPoint}
          className="map-point"
          id={`c${catNum}-${currentPoint}`}
        />,
      );
      currentPoint--;
    }

    return (
      <div
        key={secIdx}
        className="section-row"
        style={{ flex: section.count, minHeight: 0 }}
      >
        <div className="section-label">{section.label}</div>
        <div className="map-points">{points}</div>
      </div>
    );
  });
};

export default CareerMap;
